// src/pages/container_rental/components/timeline/timelineModel.ts
//
// 타임라인 DTO → 그리기용 파생값. 순수 함수만.
//
// 여기 있는 규칙은 전부 "설계 확정 사항"이므로 컴포넌트에 흩어 두지 않는다.
//  - 바 끝 규칙 (B-1)
//  - 세그먼트 기간
//  - KPI 4종
//
// 연체(overdue) 는 절대 클라이언트가 계산하지 않는다. 서버가 range.today(KST)
// 기준으로 내려 준 schedule.overdue / row.overdueCount 만 사용한다.

import type { Dayjs } from "dayjs";
import { parseKst } from "../../../../utils/date.ts";
import type {
  TimelineRowDto,
  TimelineScheduleDto,
} from "../../../../contracts/container_rental/timeline";

/** 만료 임박 기준 — D-90. */
export const EXPIRING_DAYS = 90;

export interface BarRange {
  start: Dayjs;
  end: Dayjs;
  /** 회차가 전무해서 initialMonths + (totalRounds-1) 로 추정한 구간인지 */
  estimated: boolean;
}

/**
 * 바 끝 규칙 (B-1 확정):
 *   closed && closedAt  → closedAt
 *   그 외               → cancelled 를 제외한 마지막 회차의 periodEnd
 *   회차가 전무          → rentalStartDate + initialMonths + (totalRounds-1) 개월
 */
export function barRange(row: TimelineRowDto, fallback: Dayjs): BarRange {
  const start = parseKst(row.rentalStartDate) ?? fallback;
  const notBefore = (d: Dayjs) => (d.valueOf() < start.valueOf() ? start : d);

  if (row.status === "closed" && row.closedAt) {
    const closed = parseKst(row.closedAt);
    if (closed) return { start, end: notBefore(closed), estimated: false };
  }

  const live = (row.schedules ?? []).filter((s) => s.status !== "cancelled");
  if (live.length > 0) {
    const last = live.reduce((a, b) => (b.round > a.round ? b : a));
    const end = parseKst(last.periodEnd);
    if (end) return { start, end: notBefore(end), estimated: false };
  }

  const months =
    Math.max(row.initialMonths || 0, 0) + Math.max((row.totalRounds || 0) - 1, 0);
  return { start, end: notBefore(start.add(months, "month")), estimated: true };
}

export interface SegmentModel {
  schedule: TimelineScheduleDto;
  start: Dayjs;
  end: Dayjs;
  /** 발행 예정일 — 삼각 틱 위치 */
  issue: Dayjs | null;
}

/** round ASC 로 정렬된 세그먼트 목록(기간 파싱 실패분은 제외). */
export function rowSegments(row: TimelineRowDto): SegmentModel[] {
  const out: SegmentModel[] = [];
  (row.schedules ?? []).forEach((s) => {
    const start = parseKst(s.periodStart);
    const end = parseKst(s.periodEnd);
    if (!start || !end) return;
    out.push({
      schedule: s,
      start,
      end: end.valueOf() < start.valueOf() ? start : end,
      issue: parseKst(s.issueDate),
    });
  });
  return out.sort((a, b) => a.schedule.round - b.schedule.round);
}

/** active 계약이 D-90 안에 끝나는가. */
export function isExpiringSoon(row: TimelineRowDto, today: Dayjs): boolean {
  if (row.status !== "active") return false;
  const { end } = barRange(row, today);
  const horizon = today.add(EXPIRING_DAYS, "day");
  return end.valueOf() >= today.valueOf() && end.valueOf() <= horizon.valueOf();
}

/**
 * KPI 모집단(확정) — 셋을 서로 헷갈리지 않게 여기 못박아 둔다.
 *
 *  PLAN : 계약 status == "active" 인 계약의 이번 달 scheduled 회차.
 *         서버의 overdue 규칙(active 계약만 연체를 세운다)과 모집단을 맞춘
 *         것이다. paused/draft/closed/cancelled 계약의 예정 회차는 "이번 달에
 *         실제로 발행할 것"이 아니므로 세지 않는다. → PLAN 과 OVDU 는 항상
 *         같은 모집단(active) 위에서 비교된다.
 *  DONE : 전 상태. "이미 발행됐다"는 사실 자체라 계약 상태와 무관하다.
 *         (일시정지/종료된 계약이라도 이번 달에 발행한 건 세야 한다.)
 *  OVDU : 서버 파생값 row.overdueCount 의 합 — 클라이언트가 재계산하지 않는다.
 *  EXPR : active 계약 중 D-90 안에 끝나는 계약 수(isExpiringSoon).
 */
export const PLAN_COUNTED_STATUSES: ReadonlyArray<TimelineRowDto["status"]> = [
  "active",
];

export interface TimelineKpi {
  /** 이번 달 발행 예정(scheduled) 회차 수 — active 계약만 */
  plannedThisMonth: number;
  /** 이번 달 발행 완료(issued) 회차 수 — 전 상태 */
  issuedThisMonth: number;
  /** 연체 회차 수 — 서버 파생값 합(active 계약만) */
  overdue: number;
  /** 만료 임박(D-90) 계약 수 — active 계약만 */
  expiring: number;
}

/** KPI 4종을 rows 에서 클라이언트 파생. 기준일은 서버 range.today. */
export function deriveKpi(rows: TimelineRowDto[], today: Dayjs): TimelineKpi {
  const monthStart = today.startOf("month").valueOf();
  const monthEnd = today.endOf("month").valueOf();

  let plannedThisMonth = 0;
  let issuedThisMonth = 0;
  let overdue = 0;
  let expiring = 0;

  rows.forEach((row) => {
    // PLAN 은 OVDU 와 같은 모집단(active)만 센다 — 위 주석 참조.
    const countsPlan = PLAN_COUNTED_STATUSES.includes(row.status);

    (row.schedules ?? []).forEach((s) => {
      const d = parseKst(s.issueDate);
      if (!d) return;
      const t = d.valueOf();
      if (t < monthStart || t > monthEnd) return;
      if (s.status === "scheduled") {
        if (countsPlan) plannedThisMonth += 1;
      } else if (s.status === "issued") issuedThisMonth += 1;
    });

    overdue += Number.isFinite(row.overdueCount) ? row.overdueCount : 0;
    if (isExpiringSoon(row, today)) expiring += 1;
  });

  return { plannedThisMonth, issuedThisMonth, overdue, expiring };
}

/** 원화 표기 — 기존 화면들과 동일한 가드. */
export const fmtKrw = (n: number): string =>
  Number.isFinite(n) ? new Intl.NumberFormat("ko-KR").format(n) : "—";
